import React from 'react';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useTheme,
} from '@mui/material';
import { ErrorOutline, InfoOutlined, Schedule } from '@mui/icons-material';
import type { GreeningChatAvailability } from './greeningChatAvailability';
import { GreeningBrandLabel } from './GreeningBrandLabel';
import { GREENING_AGENT_LABEL } from './constants';
import { GREENING_BRAND } from './greeningFabActions';
import { GreeningIcon } from './GreeningIcon';

interface Props {
  open: boolean;
  onClose: () => void;
  availability: GreeningChatAvailability;
}

function renderTitleWithBrand(title: string, fontWeight = 700, onDark = false) {
  const parts = title.split(GREENING_AGENT_LABEL);
  if (parts.length === 1) {
    return (
      <Typography
        variant="h6"
        component="span"
        fontWeight={fontWeight}
        sx={{ lineHeight: 1.35, color: onDark ? '#fff' : 'inherit' }}
      >
        {title}
      </Typography>
    );
  }
  return (
    <Typography
      variant="h6"
      component="span"
      fontWeight={fontWeight}
      sx={{ lineHeight: 1.35, color: onDark ? '#fff' : 'inherit' }}
    >
      {parts[0]}
      <GreeningBrandLabel fontWeight={fontWeight} onDark={onDark} />
      {parts[1]}
    </Typography>
  );
}

export function GreeningChatUnavailableDialog({ open, onClose, availability }: Props) {
  const theme = useTheme();
  const isError = availability.severity === 'error';
  const StatusIcon = isError ? ErrorOutline : Schedule;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          overflow: 'hidden',
          border: `1px solid ${alpha(GREENING_BRAND.green, 0.18)}`,
          boxShadow: `0 16px 48px ${alpha(GREENING_BRAND.green, 0.18)}`,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          background: isError
            ? alpha(theme.palette.error.main, 0.08)
            : `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`,
          color: isError ? 'text.primary' : '#fff',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            borderRadius: '50%',
            bgcolor: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 2px 12px ${alpha('#000', 0.12)}`,
          }}
        >
          <GreeningIcon sx={{ fontSize: 32, color: GREENING_BRAND.green }} />
        </Box>
        <Box sx={{ pt: 0.25, minWidth: 0 }}>
          {isError ? (
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.35 }}>
              {availability.title}
            </Typography>
          ) : (
            renderTitleWithBrand(availability.title, 800, true)
          )}
        </Box>
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(GREENING_BRAND.green, isError ? 0.04 : 0.06),
            border: `1px solid ${alpha(isError ? theme.palette.error.main : GREENING_BRAND.teal, 0.22)}`,
          }}
        >
          <StatusIcon
            sx={{
              color: isError ? 'error.main' : GREENING_BRAND.teal,
              mt: 0.15,
              flexShrink: 0,
              fontSize: 22,
            }}
          />
          <Box>
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
                lineHeight: 1.55,
                fontWeight: 500,
                fontSize: '0.95rem',
              }}
            >
              {availability.message}
            </Typography>
            {!isError && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 0.75,
                  mt: 1.75,
                  pt: 1.5,
                  borderTop: `1px solid ${alpha(GREENING_BRAND.teal, 0.15)}`,
                }}
              >
                <InfoOutlined sx={{ fontSize: 16, color: GREENING_BRAND.teal, mt: 0.15, flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Acompanhe o progresso na aba do agente{' '}
                  <GreeningBrandLabel fontWeight={600} fontSize="0.875rem" />.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{
            py: 1.1,
            fontWeight: 700,
            letterSpacing: 0.3,
            textTransform: 'none',
            fontSize: '0.95rem',
            borderRadius: 2,
            ...(isError
              ? {}
              : {
                  bgcolor: GREENING_BRAND.green,
                  background: `linear-gradient(135deg, ${GREENING_BRAND.green} 0%, ${GREENING_BRAND.teal} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(GREENING_BRAND.green, 0.35)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${GREENING_BRAND.teal} 0%, ${GREENING_BRAND.green} 100%)`,
                    boxShadow: `0 6px 18px ${alpha(GREENING_BRAND.teal, 0.4)}`,
                  },
                }),
          }}
        >
          Entendi
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GreeningChatUnavailableDialog;
